import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const AJAX_ACTIONS = {
  get: 'nova_theme_react_cart_get',
  updateItem: 'nova_theme_react_cart_update_item',
  removeItem: 'nova_theme_react_cart_remove_item',
  applyCoupon: 'nova_theme_react_cart_apply_coupon',
  removeCoupon: 'nova_theme_react_cart_remove_coupon',
}

function htmlMarkup(html) {
  return { __html: html || '' }
}

function updateHeaderCartCount(count) {
  document.querySelectorAll('span.site-navbar__cart-count, span.site-sticky-cart__count').forEach((element) => {
    element.textContent = String(count || 0)
  })
}

function normalizePath(url) {
  try {
    return new URL(url, window.location.origin).pathname.replace(/\/+$/, '') || '/'
  } catch {
    return ''
  }
}

function itemCountLabel(count, labels) {
  return `${count || 0} ${count === 1 ? labels.item || 'article' : labels.items || 'articles'}`
}

function fallbackLabel(french, english) {
  const lang = document.documentElement.lang || ''

  return lang.toLowerCase().startsWith('en') ? english : french
}

export function CartWidget({ payload }) {
  const config = useMemo(() => payload.config || {}, [payload.config])
  const labels = config.labels || {}
  const [cart, setCart] = useState(payload.cart || {})
  const [isOpen, setIsOpen] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [busyKey, setBusyKey] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState('')
  const drawerRef = useRef(null)
  const closeRef = useRef(null)

  const requestCart = useCallback(
    async (action, data = {}) => {
      const body = new window.FormData()
      body.append('action', action)
      body.append('nonce', config.ajaxNonce || '')

      Object.entries(data).forEach(([key, value]) => {
        body.append(key, value)
      })

      setError('')
      const response = await window.fetch(config.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        body,
      })
      const result = await response.json()

      if (!result.success) {
        throw new Error(result.data?.message || labels.genericError || 'Cart update failed.')
      }

      setCart(result.data)
      updateHeaderCartCount(result.data.itemsCount)
      return result.data
    },
    [config.ajaxNonce, config.ajaxUrl, labels.genericError],
  )

  const runCartAction = useCallback(
    async (key, callback) => {
      setBusyKey(key)
      setIsRefreshing(true)

      try {
        await callback()
      } catch (actionError) {
        setError(actionError.message)
      } finally {
        setBusyKey('')
        setIsRefreshing(false)
      }
    },
    [],
  )

  const openCart = useCallback(() => {
    setIsOpen(true)
    runCartAction('refresh', () => requestCart(AJAX_ACTIONS.get))
  }, [requestCart, runCartAction])

  const closeCart = useCallback(() => {
    setIsOpen(false)
  }, [])

  const updateItemQuantity = useCallback(
    (item, quantity) => {
      const nextQuantity = Math.max(Number(item.minQuantity || 0), Number(quantity || 0))
      return runCartAction(item.key, () =>
        requestCart(AJAX_ACTIONS.updateItem, {
          cart_item_key: item.key,
          quantity: nextQuantity,
        }),
      )
    },
    [requestCart, runCartAction],
  )

  const removeItem = useCallback(
    (item) =>
      runCartAction(item.key, () =>
        requestCart(AJAX_ACTIONS.removeItem, {
          cart_item_key: item.key,
        }),
      ),
    [requestCart, runCartAction],
  )

  const applyCoupon = useCallback(
    (event) => {
      event.preventDefault()

      const code = couponCode.trim()

      if (!code) {
        setError(labels.couponPlaceholder || 'Entrer un code promo')
        return
      }

      runCartAction('coupon', async () => {
        await requestCart(AJAX_ACTIONS.applyCoupon, {
          coupon_code: code,
        })
        setCouponCode('')
      })
    },
    [couponCode, labels.couponPlaceholder, requestCart, runCartAction],
  )

  const removeCoupon = useCallback(
    (coupon) => {
      if (!coupon) return

      runCartAction(`coupon-${coupon}`, () =>
        requestCart(AJAX_ACTIONS.removeCoupon, {
          coupon_code: coupon,
        }),
      )
    },
    [requestCart, runCartAction],
  )

  const handleTotalsClick = useCallback(
    (event) => {
      const couponLink = event.target.closest('.woocommerce-remove-coupon')

      if (!couponLink) {
        return
      }

      event.preventDefault()

      const coupon = couponLink.dataset.coupon || new URL(couponLink.href).searchParams.get('remove_coupon')
      removeCoupon(coupon)
    },
    [removeCoupon],
  )

  useEffect(() => {
    if (!payload.global || payload.style !== 'sidebar') return undefined

    const cartPath = config.cartUrlPath || normalizePath(config.cartUrl)

    const handleClick = (event) => {
      const link = event.target.closest?.('a')

      if (!link || link.closest('[data-nova-cart-widget]') || link.target === '_blank') return

      const isKnownCartControl =
        link.matches('.site-sticky-cart, .site-navbar__icon-button--cart, .site-navbar__mobile-cart') ||
        link.closest('.site-menu__item--cart')
      const linkPath = normalizePath(link.href)
      const pointsToCart = cartPath && linkPath === cartPath

      if (!isKnownCartControl && !pointsToCart) return

      event.preventDefault()
      openCart()
    }

    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [config.cartUrl, config.cartUrlPath, openCart, payload.global, payload.style])

  useEffect(() => {
    const handleOpenEvent = () => openCart()

    window.addEventListener('nova:open-cart-sidebar', handleOpenEvent)
    return () => window.removeEventListener('nova:open-cart-sidebar', handleOpenEvent)
  }, [openCart])

  useEffect(() => {
    if (!isOpen) return undefined

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.classList.add('nova-cart-sidebar-open')
    document.body.style.overflow = 'hidden'

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 80)

    return () => {
      window.clearTimeout(focusTimer)
      document.body.classList.remove('nova-cart-sidebar-open')
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeCart()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [closeCart, isOpen])

  const items = Array.isArray(cart.items) ? cart.items : []
  const count = Number(cart.itemsCount || 0)
  const useSidebar = payload.style === 'sidebar'

  if (!useSidebar) {
    return (
      <section className="nova-cart-widget nova-cart-widget--page">
        <div className="nova-cart-widget__summary">
          <span className="nova-cart-widget__eyebrow">{labels.title || 'Panier'}</span>
          <strong className="nova-cart-widget__count">{itemCountLabel(count, labels)}</strong>
        </div>

        <a className="nova-cart-widget__button" href={config.cartUrl}>
          {labels.cartPage || 'Voir la page panier'}
        </a>
      </section>
    )
  }

  return (
    <>
      {!payload.global ? (
        <section className="nova-cart-widget nova-cart-widget--sidebar">
          <button className="nova-cart-widget__launcher" type="button" onClick={openCart} aria-haspopup="dialog">
            <span className="nova-cart-widget__launcher-icon" aria-hidden="true">
              <i className="bi bi-bag" />
            </span>
            <span className="nova-cart-widget__summary">
              <span className="nova-cart-widget__eyebrow">{labels.title || 'Panier'}</span>
              <strong className="nova-cart-widget__count">{itemCountLabel(count, labels)}</strong>
            </span>
          </button>
        </section>
      ) : null}

      <div className={`nova-cart-sidebar${isOpen ? ' is-open' : ''}`} aria-hidden={isOpen ? 'false' : 'true'}>
        <button className="nova-cart-sidebar__backdrop" type="button" aria-label={labels.closeCart || 'Fermer le panier'} onClick={closeCart} />
        <aside
          className="nova-cart-sidebar__panel"
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label={labels.title || 'Panier'}
        >
          <header className="nova-cart-sidebar__header">
            <div>
              <span className="nova-cart-sidebar__eyebrow">{labels.title || 'Panier'}</span>
              <h2 className="nova-cart-sidebar__title">{itemCountLabel(count, labels)}</h2>
            </div>
            <button className="nova-cart-sidebar__close" type="button" ref={closeRef} onClick={closeCart} aria-label={labels.closeCart || 'Fermer le panier'}>
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </header>

          <div className="nova-cart-sidebar__body" aria-busy={isRefreshing ? 'true' : 'false'}>
            <div dangerouslySetInnerHTML={htmlMarkup(cart.noticesHtml)} />
            {error ? <div className="nova-cart-sidebar__error">{error}</div> : null}

            {cart.isEmpty || items.length === 0 ? (
              <EmptyCart labels={labels} shopUrl={config.shopUrl} />
            ) : (
              <>
                <div className="nova-cart-sidebar__items">
                  {items.map((item) => (
                    <CartSidebarItem
                      item={item}
                      isBusy={busyKey === item.key}
                      key={item.key}
                      labels={labels}
                      onQuantityChange={updateItemQuantity}
                      onRemove={removeItem}
                    />
                  ))}
                </div>

                {config.couponsEnabled ? (
                  <CouponForm
                    code={couponCode}
                    isBusy={busyKey === 'coupon'}
                    labels={labels}
                    onCodeChange={setCouponCode}
                    onSubmit={applyCoupon}
                  />
                ) : null}

                <div className="nova-cart-sidebar__totals" onClick={handleTotalsClick} dangerouslySetInnerHTML={htmlMarkup(cart.totalsHtml)} />
              </>
            )}
          </div>

          <footer className="nova-cart-sidebar__footer">
            <a className="nova-cart-sidebar__secondary" href={config.cartUrl}>
              {labels.cartPage || 'Voir la page panier'}
            </a>
            <a className="nova-cart-sidebar__checkout" href={config.checkoutUrl}>
              {labels.checkout || fallbackLabel('Commander', 'Checkout')}
            </a>
          </footer>
        </aside>
      </div>
    </>
  )
}

function CouponForm({ code, isBusy, labels, onCodeChange, onSubmit }) {
  return (
    <form className="nova-cart-sidebar__coupon" onSubmit={onSubmit}>
      <label htmlFor="nova-cart-sidebar-coupon">{labels.coupon || 'Code promo'}</label>
      <div className="nova-cart-sidebar__coupon-row">
        <input
          id="nova-cart-sidebar-coupon"
          type="text"
          value={code}
          placeholder={labels.couponPlaceholder || 'Entrer un code promo'}
          disabled={isBusy}
          onChange={(event) => onCodeChange(event.target.value)}
        />
        <button type="submit" disabled={isBusy}>
          {labels.applyCoupon || 'Appliquer'}
        </button>
      </div>
    </form>
  )
}

function EmptyCart({ labels, shopUrl }) {
  return (
    <div className="nova-cart-sidebar__empty">
      <span className="nova-cart-sidebar__empty-icon" aria-hidden="true">
        <i className="bi bi-bag" />
      </span>
      <p>{labels.emptyCart || 'Votre panier est actuellement vide !'}</p>
      {shopUrl ? (
        <a className="nova-cart-sidebar__empty-link" href={shopUrl}>
          {labels.returnToShop || 'Retour a la boutique'}
        </a>
      ) : null}
    </div>
  )
}

function CartSidebarItem({ item, isBusy, labels, onQuantityChange, onRemove }) {
  const maxQuantity = Number(item.maxQuantity || 0)
  const quantity = Number(item.quantity || 0)
  const canIncrease = maxQuantity < 1 || quantity < maxQuantity
  const canDecrease = quantity > Number(item.minQuantity || 0)

  const commitDraftQuantity = (value) => {
    const nextQuantity = Number.parseInt(value, 10)

    if (Number.isFinite(nextQuantity) && nextQuantity !== quantity) {
      onQuantityChange(item, nextQuantity)
    }
  }

  return (
    <article className="nova-cart-sidebar-item">
      <div className="nova-cart-sidebar-item__media">
        {item.permalink ? (
          <a href={item.permalink} dangerouslySetInnerHTML={htmlMarkup(item.thumbnailHtml)} />
        ) : (
          <span dangerouslySetInnerHTML={htmlMarkup(item.thumbnailHtml)} />
        )}
      </div>

      <div className="nova-cart-sidebar-item__content">
        <div className="nova-cart-sidebar-item__top">
          <h3 className="nova-cart-sidebar-item__name">
            {item.permalink ? <a href={item.permalink}>{item.name}</a> : item.name}
          </h3>
          <button
            className="nova-cart-sidebar-item__remove"
            type="button"
            disabled={isBusy}
            aria-label={`${labels.remove || 'Supprimer'} ${item.name}`}
            onClick={() => onRemove(item)}
          >
            <i className="bi bi-x" aria-hidden="true" />
          </button>
        </div>

        <div className="nova-cart-sidebar-item__meta" dangerouslySetInnerHTML={htmlMarkup(item.itemDataHtml)} />
        <div className="nova-cart-sidebar-item__price" dangerouslySetInnerHTML={htmlMarkup(item.priceHtml)} />

        <div className="nova-cart-sidebar-item__bottom">
          <div className="nova-cart-sidebar-item__quantity">
            <button
              type="button"
              disabled={isBusy || !canDecrease}
              aria-label={labels.decreaseQuantity || 'Diminuer la quantite'}
              onClick={() => onQuantityChange(item, quantity - 1)}
            >
              -
            </button>
            <input
              key={quantity}
              type="number"
              min={item.minQuantity}
              max={maxQuantity > 0 ? maxQuantity : undefined}
              defaultValue={quantity}
              disabled={isBusy || item.soldIndividually}
              aria-label={labels.quantity || 'Quantite'}
              onBlur={(event) => commitDraftQuantity(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.currentTarget.blur()
                }
              }}
            />
            <button
              type="button"
              disabled={isBusy || !canIncrease}
              aria-label={labels.increaseQuantity || 'Augmenter la quantite'}
              onClick={() => onQuantityChange(item, quantity + 1)}
            >
              +
            </button>
          </div>

          <div className="nova-cart-sidebar-item__subtotal" dangerouslySetInnerHTML={htmlMarkup(item.subtotalHtml)} />
        </div>
      </div>
    </article>
  )
}
